<template>
  <lazy-div :load="doInit">
    <v-card>
      <v-tabs v-model="tabValue" slider-color="primary">
        <v-tab @click="resetTab">List</v-tab>
        <v-tab v-show="review">Details</v-tab>
        <v-tab-item>
          <v-toolbar dense color="grey lighten-3" flat>
            <v-toolbar-title>Permitted Statistic Type per Policy Type</v-toolbar-title>
          </v-toolbar>
          <v-card-title>
            <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
            <v-spacer></v-spacer>
            <v-btn color="primary" dark class=" mr-2" @click="doNew">
              New Permitted Statistic Type
              <v-icon right>mdi-plus</v-icon>
            </v-btn>
          </v-card-title>
          <v-card>
            <v-data-table
              :headers="headers"
              :items="items"
              :search="search"
              :footer-props="{'items-per-page-options': itemsPerPage }"
              v-bind:options.sync="pagination"
              item-key="name"
              :loading="loading"
              :no-data-text="noDataText"
              :custom-filter="customSearch"
            >
              <template v-slot:item="row"
                ><tr>
                  <td class="text-xs-left">{{ row.item.polType }}</td>
                  <td class="text-xs-left">{{ row.item.polTypeDesc }}</td>
                  <td class="text-xs-left">{{ row.item.statsType }}</td>
                  <td class="text-xs-left">{{ row.item.statsUse }}</td>
                  <td class="text-xs-left">{{ row.item.permTranType }}</td>
                  <td class="text-xs-left">{{ row.item.defStatsVal }}</td>
                  <td class="table-action-header">
                    <table-action
                      :rowData="row"
                      review
                      @review="doReview(row.item)"
                      query
                      @query="doQuery(row.item)"
                      remove
                      @remove="doDelete(row.item)"
                    ></table-action>
                  </td></tr
              ></template>
            </v-data-table>
          </v-card>
          <confirm ref="confirmDelete"> Are you sure you want to delete the selected record? ({{ selectedPolTypeStats }}) </confirm>
        </v-tab-item>
        <v-tab-item v-show="review">
          <v-card flat>
            <v-toolbar dense color="grey lighten-3" flat>
              <v-icon v-if="newPolTypeStats">mdi-plus</v-icon>
              <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
              <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
              <v-toolbar-title v-if="newPolTypeStats">Create Statistic Type per Policy Type</v-toolbar-title>
              <v-toolbar-title v-else-if="reviewMode">Edit Statistic Type per Policy Type</v-toolbar-title>
              <v-toolbar-title v-else-if="queryMode">Query Statistic Type per Policy Type</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-container fluid grid-list-lg>
                <message :messages="messages"></message>
                <v-layout wrap>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">{{ labels.statsType }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.statsType }} - {{ polTypeStatsForm.statsTypeDesc }}</div>
                  </v-flex>
                  <v-flex xs6 v-if="newPolTypeStats">
                    <v-text-field
                      :readonly="queryMode"
                      :label="labels.polType"
                      v-model="polTypeStatsForm.polType"
                      :error-messages="errors.collect('polType')"
                      data-vv-name="polType"
                      :data-vv-as="labels.polType"
                      v-validate="'required|max:4'"
                      append-icon="mdi-magnify"
                      @click:append="doHelplist('polType')"
                      :hint="polTypeStatsForm.polTypeDesc"
                      persistent-hint
                      @change="doCheck('polType')"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs6 v-else>
                    <v-subheader class="queryLabel">{{ labels.polType }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.polType }} - {{ polTypeStatsForm.polTypeDesc }}</div>
                  </v-flex>
                </v-layout>
                <v-layout wrap>
                  <v-flex xs6 v-if="newPolTypeStats">
                    <v-autocomplete
                      :readonly="queryMode"
                      :label="labels.statsUse"
                      :items="statsUseList"
                      item-text="label"
                      item-value="value"
                      v-model="polTypeStatsForm.statsUse"
                      :error-messages="errors.collect('statsUse')"
                      data-vv-name="statsUse"
                      :data-vv-as="labels.statsUse"
                      v-validate="'required|max:1'"
                      disabled
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs6 v-else>
                    <v-subheader class="queryLabel">{{ labels.statsUse }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.statsUse }} - {{ polTypeStatsForm.statsUseDesc }}</div>
                  </v-flex>
                  <v-flex xs6 v-if="newPolTypeStats">
                    <v-autocomplete
                      :readonly="queryMode"
                      :label="labels.permTranType"
                      :items="permTranTypeList"
                      item-text="label"
                      item-value="value"
                      v-model="polTypeStatsForm.permTranType"
                      :error-messages="errors.collect('permTranType')"
                      data-vv-name="permTranType"
                      :data-vv-as="labels.permTranType"
                      v-validate="'required'"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs6 v-else>
                    <v-subheader class="queryLabel">{{ labels.permTranType }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.permTranTypeDesc }}</div>
                  </v-flex>
                  <v-flex xs6>
                    <v-text-field
                      :readonly="queryMode"
                      :label="labels.defStatsVal"
                      v-model="polTypeStatsForm.defStatsVal"
                      :error-messages="errors.collect('defStatsVal')"
                      data-vv-name="defStatsVal"
                      :data-vv-as="labels.defStatsVal"
                      v-validate="'max:4'"
                      append-icon="mdi-magnify"
                      @click:append="doHelplist('statsVal')"
                      :hint="polTypeStatsForm.defStatsValDesc"
                      persistent-hint
                      @change="doCheck('defStatsVal')"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout wrap>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.createdBy }}</div>
                  </v-flex>
                  <v-flex xs6>
                    <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.createdDate | formatDate }}</div>
                  </v-flex>
                  <v-flex xs6 v-if="!newPolTypeStats">
                    <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.modifiedBy }}</div>
                  </v-flex>
                  <v-flex xs6 v-if="!newPolTypeStats">
                    <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                    <div class="queryValue">{{ polTypeStatsForm.modifiedDate | formatDate }}</div>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
            <v-btn class="mr-2 mb-2" color="error" @click="doCancel" :loading="loading">
              <span>Cancel</span>
              <v-icon right dark>mdi-cancel</v-icon>
            </v-btn>
            <v-btn color="primary" v-if="!queryMode" dark class="mr-2 mb-2" @click="doSave">
              Save Statistic Type
              <v-icon right>mdi-content-save</v-icon>
            </v-btn>
            <v-dialog v-model="helplist" width="800px" persistent>
              <helplist
                :key="helplist"
                :apiURI="helplistURI"
                :fieldList="helplistField"
                :fieldKey1="helplistFieldKey"
                :expand="helplistExpand"
                @select="doSelect($event)"
                @close="doClose"
              ></helplist>
            </v-dialog>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>
  </lazy-div>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import { formatDisplayDate } from '@/utils/date'
import { format, toDate } from 'date-fns'
import Helplist from '../helplist/helplist'
export default {
  data() {
    return {
      headers: [
        { text: 'Policy Type', value: 'polType' },
        { text: 'Policy Type Description', value: 'polTypeDesc' },
        { text: 'Statistic Type', value: 'statsType' },
        { text: 'Statistic Use', value: 'statsUse' },
        { text: 'Permitted Tran Type', value: 'permTranType' },
        { text: 'Default Statistic Value', value: 'defStatsVal' },
        TableConst.tableActionHeader
      ],
      labels: {
        polType: 'Policy Type',
        statsType: 'Statistic Type',
        statsUse: 'Statistic Use',
        defStatsVal: 'Default Statistic Value',
        permTranType: 'Permitted Transaction Type',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      polTypeStatsForm: {
        polType: null,
        polTypeDesc: null,
        statsType: null,
        statsTypeDesc: null,
        statsUse: null,
        defStatsVal: null,
        defStatsValDesc: null,
        permTranType: null,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null,
        statsUseDesc: null,
        permTranTypeDesc: null
      },
      tabValue: 0,
      review: false,
      newPolTypeStats: false,
      statsUseList: [{ label: 'Policy Header', value: '4' }],
      permTranTypeList: [
        { label: 'ALL', value: 'A' },
        { label: 'N - New', value: 'N' },
        { label: 'E - Endorsement', value: 'E' },
        { label: 'C - Cancellation', value: 'C' },
        { label: 'R - Renewal', value: 'R' }
      ],
      helplistMenu: null,
      helplist: false,
      helplistURI: null,
      helplistField: null,
      helplistFieldKey: null,
      helplistExpand: null,
      selectedPolTypeStats: null,
      queryMode: false,
      reviewMode: false,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  created() {
    this.fetchStatsType()
    this.loadHelplist()
  },
  methods: {
    async loadHelplist() {
      const apiURI = this.menu.polTypeStatsHelplist
      if (apiURI) {
        try {
          const expand = {
            statsType: this.statsType
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          this.helplistMenu = extractMenu(result.data._links)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async fetchStatsType() {
      const apiURI = this.menu.statsType
      if (apiURI) {
        try {
          const expand = {
            statsType: this.statsType
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.polTypeStatsForm.statsType = data.statsType.statsType
            this.polTypeStatsForm.statsTypeDesc = data.statsType.statsTypeDesc
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doInit() {
      const apiURI = this.menu.polTypeStatsList
      if (apiURI) {
        try {
          this.loading = true
          const expand = {
            statsType: this.statsType
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          this.items = []
          const { data } = result.data
          if (data) {
            data.map((polTypeStats) => {
              const convertList = {
                polType: polTypeStats.polType,
                polTypeDesc: polTypeStats.polTypeDesc,
                statsType: polTypeStats.statsType,
                statsUse: polTypeStats.statsUse,
                defStatsVal: polTypeStats.defStatsVal,
                permTranType: polTypeStats.permTranType
              }
              this.items.push(convertList)
            })
          }
          this.loading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doReview(data) {
      this.reviewMode = true
      this.queryMode = false
      this.doFetchData(data)
    },
    async doQuery(data) {
      this.reviewMode = false
      this.queryMode = true
      this.doFetchData(data)
    },
    doFetchData(data) {
      if (data) {
        const apiURI = this.menu.polTypeStats
        if (apiURI) {
          const expand = {
            statsType: data.statsType,
            polType: data.polType,
            statsUse: data.statsUse,
            permTranType: data.permTranType
          }
          this.$http
            .get(expandURI(apiURI, expand))
            .then((result) => {
              const { data } = result.data
              if (data) {
                this.polTypeStatsForm.polType = data.polTypeStats.polType
                if (data.polType) {
                  this.polTypeStatsForm.polTypeDesc = data.polType.polTypeDesc
                }
                this.polTypeStatsForm.statsType = data.polTypeStats.statsType
                this.polTypeStatsForm.statsUse = data.polTypeStats.statsUse
                this.polTypeStatsForm.statsUseDesc = this.displayLabel(data.polTypeStats.statsUse, 'statsUse')
                this.polTypeStatsForm.defStatsVal = data.polTypeStats.defStatsVal
                this.polTypeStatsForm.permTranType = data.polTypeStats.permTranType
                this.polTypeStatsForm.permTranTypeDesc = this.displayLabel(data.polTypeStats.permTranType, 'permTranType')
                if (data.statsVal) {
                  this.polTypeStatsForm.defStatsValDesc = data.statsVal.statsValDesc
                }
                this.polTypeStatsForm.createdBy = data.createdBy
                this.polTypeStatsForm.createdDate = data.createdDate
                this.polTypeStatsForm.modifiedBy = data.modifiedBy
                this.polTypeStatsForm.modifiedDate = data.modifiedDate
                this.review = true
                this.tabValue = 1
                this.newPolTypeStats = false
              }
            })
            .catch((err) => {
              const { data } = err.response
              if (data) {
                this.message(data)
                this.fieldMessage(data)
              }
            })
        }
      }
    },
    doNew() {
      this.tabValue = 1
      this.review = true
      this.newPolTypeStats = true
      this.polTypeStatsForm.polType = null
      this.polTypeStatsForm.polTypeDesc = null
      this.polTypeStatsForm.statsUse = '4'
      this.polTypeStatsForm.defStatsVal = ''
      this.polTypeStatsForm.defStatsValDesc = null
      this.polTypeStatsForm.permTranType = null
      this.polTypeStatsForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.polTypeStatsForm.createdDate = format(toDate(Date.now()), 'yyyy-MM-dd')
    },
    resetTab() {
      this.tabValue = 0
      this.review = false
      this.newPolTypeStats = false
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          const config = {
            method: this.newPolTypeStats ? 'post' : 'put',
            url: this.newPolTypeStats ? this.menu.createPolTypeStats : this.menu.updatePolTypeStats,
            data: this.polTypeStatsForm
          }
          if (config.url) {
            this.$http
              .request(config)
              .then((result) => {
                const { data } = result
                this.notify(data)
                this.resetTab()
                this.doInit()
              })
              .catch((err) => {
                const { data } = err.response
                if (data) {
                  this.message(data)
                  this.fieldMessage(data)
                }
              })
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doDelete(data) {
      this.resetMessages()
      if (data) {
        const expand = {
          statsType: data.statsType,
          polType: data.polType,
          statsUse: data.statsUse,
          permTranType: data.permTranType
        }
        const apiURI = this.menu.checkDeletePolTypeStats
        const apiURI2 = this.menu.deletePolTypeStats
        if (apiURI && apiURI2) {
          try {
            await this.$http.get(expandURI(apiURI, expand))
            this.selectedPolTypeStats = data.polType
            const prompt = await this.$refs.confirmDelete.doPopUp()
            if (prompt) {
              const result = await this.$http.delete(expandURI(apiURI2, expand))
              const { data } = result
              this.notify(data)
              this.doInit()
            }
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.notify(data)
            }
          }
        }
      }
    },
    doHelplist(field) {
      if (!this.queryMode) {
        this.resetMessages()
        this.resetValidator()
        switch (field) {
          case 'statsVal': {
            this.helplistURI = this.menu.statsValList
            this.helplistExpand = {
              statsType: this.statsType
            }
            this.helplistField = [
              { text: 'Statistic Type', value: 'statsType' },
              { text: 'Statistic Value', value: 'statsVal' },
              { text: 'Description', value: 'statsValDesc' }
            ]
            this.helplistFieldKey = field
            this.helplist = true
            break
          }
          case 'polType': {
            this.helplistURI = this.helplistMenu.polTypeHelplist
            this.helplistField = [
              { text: 'Policy Type', value: 'polType' },
              { text: 'Description', value: 'polTypeDesc' }
            ]
            this.helplistFieldKey = field
            this.helplist = true
          }
        }
      }
    },
    doSelect(result) {
      if (result && result.fieldKey1 && result.data) {
        switch (result.fieldKey1) {
          case 'statsVal': {
            this.polTypeStatsForm.defStatsVal = result.data.statsVal
            this.polTypeStatsForm.defStatsValDesc = result.data.statsValDesc
            break
          }
          case 'polType': {
            this.polTypeStatsForm.polType = result.data.polType
            this.polTypeStatsForm.polTypeDesc = result.data.polTypeDesc
          }
        }
      }
    },
    doValidate(field) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(this.fields[field].valid)
        }, 100)
      })
    },
    doCheck(field) {
      this.doValidate(field).then((result) => {
        if (result) {
          let key = field
          let desc = null
          let desc2 = null
          let apiURI = null
          let expand = {}
          switch (field) {
            case 'defStatsVal': {
              if (!this.polTypeStatsForm.defStatsVal) {
                this.polTypeStatsForm.defStatsValDesc = null
                return
              }
              key = 'statsVal'
              apiURI = this.menu.statsVal
              expand = {
                statsType: this.statsType,
                statsVal: this.polTypeStatsForm.defStatsVal
              }
              desc = 'defStatsValDesc'
              desc2 = 'statsValDesc'
              break
            }
            case 'polType': {
              apiURI = this.helplistMenu.polType
              expand.polType = this.polTypeStatsForm.polType
              desc = 'polTypeDesc'
            }
          }
          this.polTypeStatsForm[desc] = null
          if (!desc2) {
            desc2 = desc
          }
          if (apiURI) {
            this.$http
              .get(expandURI(apiURI, expand))
              .then((result) => {
                const { data } = result.data
                if (data) {
                  this.polTypeStatsForm[desc] = data[key][desc2]
                }
              })
              .catch((err) => {
                const { data } = err.response
                if (data) {
                  this.notify(data)
                  this.polTypeStatsForm[field] = null
                }
              })
          }
        }
      })
    },
    doClose() {
      this.helplist = false
    },
    doCancel() {
      this.resetTab()
      this.doInit()
    },
    displayLabel(value, field) {
      var list = []
      var returnLabel = ''
      if (field === 'statsUse') {
        list = this.statsUseList
      } else if (field === 'permTranType') {
        list = this.permTranTypeList
      }

      for (var i in list) {
        if (list[i].value === value) {
          returnLabel = list[i].label
        }
      }
      return returnLabel
    }
  },
  computed: {
    menu() {
      return this.$store.state.statsTypeParam.menu
    }
  },
  filters: {
    formatDate(value) {
      if (value) {
        return formatDisplayDate(value)
      } else {
        return null
      }
    }
  },
  components: {
    Helplist
  },
  mixins: [TableConst.default, MessageMixin],
  props: ['statsType']
}
</script>

<style>
</style>
